import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    IconButton,
    Box,
    useMediaQuery,
    useTheme,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Container,
    Theme,
    Avatar,
    Divider,
    Badge,
    Tooltip,
    Popover,
    ToggleButton,
    ToggleButtonGroup,
    alpha,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import ExploreRoundedIcon from '@mui/icons-material/ExploreRounded';
import { Chat } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';
import { paths } from '../routes/paths';
import { SessionContext } from '../SessionContext';
import { useApiClient } from '../ApiClientProvider';
import { ProfileService } from '../services/profileService';
import { NotificationService } from '../services/notificationService';
import ChatConnections from './chat/ChatConnections';
import { logEvent } from '../utils/analytics';
import { NotificationBell } from './NotificationBell';
import HeaderSearchBar, { HeaderSearchButton } from './HeaderSearchBar';
import { useNotificationHub } from '../hooks/useNotificationHub';
import { IUser } from '../interfaces/IChatUser';
import { DEFAULT_AVATAR_SRC } from '../helpers/avatarColor';

export const ChatButton: React.FC<{ show?: boolean; sx?: object }> = ({ show = true, sx = {} }) => {
    const [connectionsPopoverAnchor, setConnectionsPopoverAnchor] = useState<null | HTMLElement>(null);
    const [connectionsPopoverOpen, setConnectionsPopoverOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [pendingRequestsCount, setPendingRequestsCount] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            if (window.chatManager) {
                const counts = window.chatManager.getUnreadCounts();
                setUnreadCount(Object.values(counts).reduce((sum, c) => sum + c, 0));
                const pendingRequests = window.chatManager.getConnectionRequests?.()
                    .filter(req => !req.isRequestedByMe).length || 0;
                setPendingRequestsCount(pendingRequests);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    if (!show) return null;

    const handleConnectionsClick = (event: React.MouseEvent<HTMLElement>) => {
        setConnectionsPopoverAnchor(event.currentTarget);
        setConnectionsPopoverOpen(true);
    };
    const handleConnectionsClose = () => {
        setConnectionsPopoverAnchor(null);
        setConnectionsPopoverOpen(false);
    };
    const handleStartChat = (user: IUser) => {
        window.chatManager?.startChat(user);
        handleConnectionsClose();
    };
    const totalCount = unreadCount + pendingRequestsCount;

    return (
        <>
            <Tooltip
                title={
                    totalCount > 0
                        ? `Chat${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}${pendingRequestsCount > 0 ? ` (${pendingRequestsCount} request${pendingRequestsCount > 1 ? 's' : ''})` : ''}`
                        : 'Chat'
                }
                enterDelay={1000}
            >
                <IconButton
                    type="button"
                    aria-label={
                        totalCount > 0
                            ? `Chat: ${unreadCount} unread message${unreadCount !== 1 ? 's' : ''}, ${pendingRequestsCount} pending request${pendingRequestsCount !== 1 ? 's' : ''}`
                            : 'Chat'
                    }
                    onClick={handleConnectionsClick}
                    sx={sx}
                >
                    <Badge
                        badgeContent={totalCount}
                        color="error"
                        max={99}
                        sx={{
                            '& .MuiBadge-badge': {
                                fontSize: '0.7rem',
                                height: '18px',
                                minWidth: '18px',
                                fontWeight: 600,
                            }
                        }}
                    >
                        <Chat />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                open={connectionsPopoverOpen}
                anchorEl={connectionsPopoverAnchor}
                onClose={handleConnectionsClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: {
                            width: 400,
                            height: 600,
                            maxHeight: '80vh',
                            overflow: 'hidden',
                            borderRadius: 2,
                            boxShadow: '08px32px rgba(0,0,0,0.12)',
                            '@media (max-width:768px)': {
                                width: 320,
                                height: 500,
                            }
                        }
                    }
                }}
            >
                <ChatConnections
                    onStartChat={handleStartChat}
                    onAcceptRequest={(id: string) => window.chatManager?.acceptRequest(id)}
                    onRejectRequest={(id: string) => window.chatManager?.rejectRequest(id)}
                    onCancelRequest={(id: string) => window.chatManager?.cancelRequest(id)}
                    onOpenPodChat={(podId: string) => {
                        window.podChatManager?.openPod(podId);
                        handleConnectionsClose();
                    }}
                />
            </Popover>
        </>
    );
};

const Header: React.FC = () => {
    const { session, signOut } = useContext(SessionContext);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [profileImage, setProfileImage] = useState<string>(""); // Fixed: store avatar here
    const [mode, setMode] = useState<'web' | 'management'>('web');
    const [isScrolled, setIsScrolled] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

    const managementClient = useApiClient('managementClient');
    const applicationClient = useApiClient('applicationClient');
    const profileService = useMemo(() => new ProfileService(managementClient), [managementClient]);
    const notificationService = useMemo(
        () => new NotificationService(applicationClient, 'participant'),
        [applicationClient],
    );
    const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

    useNotificationHub({
        enabled: !!session?.token,
        token: session?.token || '',
        hubTarget: 'participant',
        onNotificationReceived: () => {
            setUnreadNotificationCount((prev) => prev + 1);
        },
        onUnreadCountUpdated: (count) => {
            setUnreadNotificationCount(count);
        },
        onReconnected: () => {
            // Reconcile the unread count after a reconnect — notifications broadcast while the
            // socket was down are not replayed by SignalR.
            notificationService
                .getUnreadCount()
                .then(setUnreadNotificationCount)
                .catch((error) => console.error('Failed to reconcile notification count after reconnect:', error));
        },
    });

    // Fetch current user profile image
    useEffect(() => {
        const fetchProfile = async () => {
            if (session?.user) {
                try {
                    const image = await profileService.getProfileImage();
                    setProfileImage(image || "");
                } catch (err) {
                    console.error("Failed to fetch profile image", err);
                    setProfileImage("");
                }
            }
        };

        fetchProfile();

        const handleScroll = () => setIsScrolled(window.scrollY > 10);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [mode, profileService, session]);

    useEffect(() => {
        const loadUnreadCount = async () => {
            if (!session?.token) {
                setUnreadNotificationCount(0);
                return;
            }

            try {
                const count = await notificationService.getUnreadCount();
                setUnreadNotificationCount(count);
            } catch (error) {
                console.error('Failed to load public-header notification count:', error);
            }
        };

        loadUnreadCount();
    }, [notificationService, session?.token]);

    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

    const handleSignIn = () => navigate(paths.signIn);
    const handleSignUp = () => navigate(paths.signUp);
    const handleLogout = () => {
        signOut();
        setProfileOpen(false);
        setMobileOpen(false);
        navigate('/');
    };

    const handleModeChange = (
        event: React.MouseEvent<HTMLElement>,
        newMode: 'web' | 'management' | null,
    ) => {
        if (newMode) {
            setMode(newMode);
            setProfileOpen(false);
            if (newMode === 'management') navigate(paths.dashboard);
            else navigate('/');
        }
    };

    // Matches on pathname, and (when `path` carries a querystring, e.g. `/search?type=organisations`) on those query params too.
    const isActive = (path: string) => {
        const [pathOnly, queryString] = path.split('?');
        if (location.pathname !== pathOnly) return false;
        if (!queryString) return true;
        const expected = new URLSearchParams(queryString);
        const actual = new URLSearchParams(location.search);
        return Array.from(expected.entries()).every(([key, value]) => actual.get(key) === value);
    };

    // Show member-oriented nav for any logged-in user
    const isLoggedInMember = !!session;

    // Per TL requirement: only these 3 top-level tabs, always (not just while searching).
    const memberNavItems = [
        { label: 'Home', path: '/', icon: <HomeRoundedIcon fontSize="small" /> },
        { label: 'Discover', path: paths.public.search, icon: <ExploreRoundedIcon fontSize="small" /> },
        { label: 'Network', path: paths.public.network, icon: <PeopleAltRoundedIcon fontSize="small" /> },
        { label: 'My Events', path: paths.public.myEvents, icon: <ConfirmationNumberRoundedIcon fontSize="small" /> },
    ];

    const drawerProfileLoggedIn = (
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box display="flex" justifyContent="flex-end">
                <IconButton onClick={() => setProfileOpen(false)}><CloseIcon /></IconButton>
            </Box>

            <Box display="flex" flexDirection="column" alignItems="center" mt={2} mb={2}>
                <Avatar
                    src={profileImage || DEFAULT_AVATAR_SRC}
                    alt="profile"
                    sx={{ width: 80, height: 80, border: '3px solid #1976d2' }}
                />
                <Typography variant="h6" sx={{ mt: 1 }}>{session?.user?.name}</Typography>
                <Typography variant="body2" color="text.secondary">{session?.user?.email}</Typography>

                <Button
                    variant="outlined"
                    size="small"
                    sx={{
                        mt: 1,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '0.7rem',
                        px: 2,
                        py: 0.5,
                        borderColor: 'grey.300',
                        '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            color: 'primary.main',
                        },
                    }}
                    onClick={() => {
                        setProfileOpen(false);
                        navigate(`${paths.public.profile}?tab=editProfile`);
                    }}
                >
                    Edit Profile
                </Button>

                {(session?.roles?.includes('Admin') || session?.roles?.includes('User')) && (
                    <ToggleButtonGroup
                        value={mode}
                        exclusive
                        onChange={handleModeChange}
                        fullWidth
                        size="small"
                        sx={{ mt: 2 }}
                    >
                        <ToggleButton value="web">AttendIn</ToggleButton>
                        <ToggleButton value="management">Management</ToggleButton>
                    </ToggleButtonGroup>
                )}
            </Box>

            <Divider sx={{ mb: 1, mt: 2 }} />
            <List sx={{ flexGrow: 1 }}>
                {session?.roles?.includes('Partner') && (
                    <ListItem disablePadding>
                        <ListItemButton onClick={() => { setProfileOpen(false); navigate(paths.public.partnerDashboard); logEvent('Header', 'click', 'Partner Dashboard'); }} sx={{ borderRadius: 2 }}>
                            <ListItemText primary="Partner Dashboard" />
                        </ListItemButton>
                    </ListItem>
                )}
                <ListItem disablePadding>
                    <ListItemButton onClick={() => { setProfileOpen(false); navigate(paths.public.myEvents); logEvent('Header', 'click', 'Saved'); }} sx={{ borderRadius: 2 }}>
                        <ListItemText primary="Saved" />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton onClick={() => { setProfileOpen(false); navigate(paths.public.tickets); logEvent('Header', 'click', 'My Tickets'); }} sx={{ borderRadius: 2 }}>
                        <ListItemText primary="My Tickets" />
                    </ListItemButton>
                </ListItem>
            </List>

            <Button
                variant="contained"
                color="error"
                onClick={handleLogout}
                sx={{ borderRadius: 2, mt: 'auto', mb: 2 }}
            >
                Logout
            </Button>
        </Box>
    );

    const drawerMobile = (
        <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                <Box component="img" src="/logo-only.png" alt="Attendin logo" title="AttendIn" sx={{ height: 50 }} />
                <IconButton edge="end" color="inherit" aria-label="close"><CloseIcon /></IconButton>
            </Box>
            <List>
                {(isLoggedInMember ? memberNavItems : [
                    { label: 'Search Events', path: paths.public.searchEvents },
                    { label: 'Event Organisers', path: paths.public.eventOrganizers },
                    { label: 'Pricing', path: paths.public.betaProgram },
                    { label: 'Contact Us', path: paths.public.contact },
                ]).map((item) => (
                    <ListItem disablePadding key={item.label}>
                        <ListItemButton onClick={() => { navigate(item.path); logEvent('Header', 'click', item.label); }} sx={{ textAlign: 'center', gap: 1.5 }}>
                            {'icon' in item && item.icon}
                            <ListItemText primary={item.label} />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </Box>
    );

    return (
        <>
            <AppBar
                position="fixed"
                color="default"
                elevation={isScrolled ? 4 : 0}
                sx={{
                    top: { xs: 0, sm: isScrolled ? theme.spacing(2) : 0 },
                    left: { xs: 0, sm: isScrolled ? theme.spacing(2) : 0 },
                    right: { xs: 0, sm: isScrolled ? theme.spacing(2) : 0 },
                    width: { xs: '100%', sm: isScrolled ? `calc(100% - ${theme.spacing(4)})` : '100%' },
                    margin: 'auto',
                    backgroundColor: isScrolled ? 'rgba(255, 255, 255, 0.5)' : 'background.paper',
                    backdropFilter: isScrolled ? 'blur(20px)' : 'none',
                    border: '0.5px solid',
                    borderColor: 'divider',
                    borderRadius: '12px',
                    transition: 'all 0.3s ease-in-out',
                    zIndex: (theme) => theme.zIndex.appBar + 1,
                }}
            >
                <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 3 } }}>
                    <Toolbar disableGutters sx={{ display: 'grid', gridTemplateColumns: { xs: 'auto minmax(0, 1fr)', md: 'minmax(0, 1fr) auto minmax(0, 1fr)' }, alignItems: 'center', gap: { xs: 0.5, sm: 2 } }}>
                        {/* Logo and Mobile Menu */}
                        <Box sx={{ display: 'flex', minWidth: 0 }}>
                            {isMobile && (
                                <IconButton color="inherit" aria-label="Open navigation" onClick={handleDrawerToggle} sx={{ mr: { xs: 0, sm: 1 }, minWidth: 'var(--touch-min)', minHeight: 'var(--touch-min)' }}>
                                    <MenuIcon />
                                </IconButton>
                            )}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: 'auto',
                                    flexShrink: 0,
                                    cursor: 'pointer',
                                    justifyContent: 'center',
                                    minWidth: 'var(--touch-min)',
                                    minHeight: 'var(--touch-min)',
                                }}
                                onClick={() => navigate('/')}
                            >
                                <Box component="img" src="/logo-only.png" alt="Attendin logo small" title="AttendIn" sx={{ display: { xs: 'block', md: 'none' }, height: 32 }} />
                                <Box
                                    component="img"
                                    src="/logo-only.png"
                                    alt="Attendin logo"
                                    title="AttendIn"
                                    sx={{ display: { xs: 'none', md: 'block' }, height: 34 }}
                                />
                            </Box>
                            {isLoggedInMember && (
                                <HeaderSearchBar
                                    key={location.pathname === paths.public.search
                                        ? `${location.pathname}${location.search}`
                                        : location.pathname}
                                />
                            )}
                        </Box>

                        {/* Center nav (desktop) */}
                        {!isMobile && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                {isLoggedInMember ? (
                                    // Member Hub navigation for logged-in users
                                    memberNavItems.map((item) => (
                                        <Button
                                            key={item.label}
                                            onClick={() => { navigate(item.path); logEvent('Header', 'click', item.label); }}
                                            variant="text"
                                            aria-current={isActive(item.path) ? 'page' : undefined}
                                            startIcon={item.icon}
                                            sx={{
                                                color: isActive(item.path) ? 'primary.main' : 'text.secondary',
                                                borderBottom: 2,
                                                borderColor: isActive(item.path) ? 'primary.main' : 'transparent',
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                px: 2.5,
                                                borderRadius: '50px',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    bgcolor: 'action.hover',
                                                },
                                            }}
                                        >
                                            {item.label}
                                        </Button>
                                    ))
                                ) : (
                                    // Default public navigation
                                    [
                                        { key: 'searchEvents', label: 'Search Events', path: paths.public.searchEvents },
                                        { key: 'organisations', label: 'Event Organisers', path: paths.public.eventOrganizers },
                                        { key: 'betaProgram', label: 'Pricing', path: paths.public.betaProgram },
                                        { key: 'contact', label: 'Contact Us', path: paths.public.contact },
                                    ].map(({ key, label, path }) => (
                                        <Button
                                            key={key}
                                            onClick={() => {
                                                navigate(path);
                                                logEvent('Header', 'click', key);
                                            }}
                                            variant={isActive(path) ? 'contained' : 'text'}
                                            sx={{
                                                color: isActive(path) ? 'primary.contrastText' : 'primary.main',
                                                bgcolor: isActive(path) ? 'primary.main' : 'transparent',
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                px: 2.5,
                                                borderRadius: '50px',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    bgcolor: isActive(path) ? 'primary.dark' : alpha(theme.palette.primary.main, 0.08),
                                                    transform: 'translateY(-1px)',
                                                }
                                            }}
                                        >
                                            {label}
                                        </Button>
                                    ))
                                )}
                            </Box>
                        )}

                        {/* Right Section */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: { xs: 0.5, sm: 1 }, '& .MuiIconButton-root': { minWidth: 'var(--touch-min)', minHeight: 'var(--touch-min)' } }}>
                            {isLoggedInMember && <HeaderSearchButton />}
                            <ChatButton show={!!session} sx={{ mr: { xs: 0, sm: 1 } }} />
                            {session ? (
                                <>
                                    <NotificationBell
                                        unreadCount={unreadNotificationCount}
                                        scope="participant"
                                        onCountChange={setUnreadNotificationCount}
                                    />
                                    <Button
                                        aria-label="Open your account"
                                        onClick={() => setProfileOpen(true)}
                                        sx={{
                                            minWidth: 'var(--touch-min)',
                                            minHeight: 'var(--touch-min)',
                                            px: 0.5,
                                            borderRadius: '20px',
                                            '&:hover': { backgroundColor: 'rgba(37, 99, 235, 0.04)' },
                                        }}
                                    >
                                        <Avatar src={profileImage || DEFAULT_AVATAR_SRC} sx={{ width: theme => theme.spacing(4), height: theme => theme.spacing(4), typography: 'caption' }} />
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button variant="outlined" color="primary" onClick={handleSignIn}>Login</Button>
                                    <Button variant="contained" color="primary" onClick={handleSignUp}>Sign Up</Button>
                                </>
                            )}
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>
            <Toolbar />

            <Drawer
                variant="temporary"
                anchor="right"
                open={profileOpen}
                onClose={() => setProfileOpen(false)}
                ModalProps={{ keepMounted: true }}
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 2, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 300 } }}
            >
                {drawerProfileLoggedIn}
            </Drawer>
            <Drawer
                variant="temporary"
                anchor="left"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
                sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 } }}
            >
                {drawerMobile}
            </Drawer>
        </>
    );
};

export default Header;